import { Stack, Text, Timeline } from "@mantine/core";
import { ClockIcon, CircleDashedIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { i18n } from "@dehub/theme";
import type { IReturnTimelineEntry, IReturnPendingStep } from "../../apis/return";
import { dateTimeSecFormat } from "../../utils/date";
import classes from "./timeline.module.css";

// Lining figures so the timestamps line up vertically.
const TNUM = { fontVariantNumeric: "tabular-nums" } as const;

// B70/B71: some entries carry a raw enum code as the note (e.g. a resolution
// type); show its translation when one exists, otherwise the note unchanged.
const noteLabel = (notes: string): string => {
  const key = `returns:resolutionType.${notes}`;
  return /^[A-Z_]+$/.test(notes) && i18n.exists(key) ? i18n.t(key) : notes;
};

export const actionLabel = (action: string): string =>
  i18n.t(`returns:timeline.action.${action}`, { defaultValue: "-" });

interface Props {
  entries: IReturnTimelineEntry[];
  // §9.2 §5 — future (○) steps derived from the state machine (no timestamp).
  pendingSteps?: IReturnPendingStep[];
  /** "plain" = buyer canvas look: dark filled dot for done, hollow grey circle for pending, thin solid line. */
  variant?: "default" | "plain";
}

export function ReturnTimeline ({ entries, pendingSteps = [], variant = "default" }: Props){
  const { t } = useTranslation("returns");
  if (!entries.length && !pendingSteps.length) {
    return <Text c="dimmed">{t("detail.noRecords")}</Text>;
  }

  // Past events fill the line up to the latest one; pending steps stay unfilled.
  const active = entries.length - 1;

  if (variant === "plain") {
    return (
      <Timeline active={active} bulletSize={14} lineWidth={1} color="dark" classNames={{ item: classes.plainItem, itemBullet: classes.plainBullet }}>
        {entries.map((entry, idx) => (
          <Timeline.Item
            key={`${entry.action}-${entry.timestamp}-${idx}`}
            title={<Text size="sm" fw={700} ff="monospace" style={TNUM}>{dateTimeSecFormat(entry.timestamp)}</Text>}
          >
            <Stack gap={2}>
              <Text size="sm" fw={600}>{actionLabel(entry.action)}</Text>
              {entry.actorName ? <Text size="sm" c="dimmed">{t("timeline.by", { name: entry.actorName })}</Text> : null}
              {entry.notes ? <Text size="sm" c="dimmed">{noteLabel(entry.notes)}</Text> : null}
            </Stack>
          </Timeline.Item>
        ))}

        {pendingSteps.map((step) => (
          <Timeline.Item
            key={`pending-${step.status}`}
            color="gray"
            title={<Text size="sm" fw={500}>{step.label}</Text>}
          >
            <Text size="sm" c="dimmed">{t("timeline.pendingStep")}</Text>
          </Timeline.Item>
        ))}
      </Timeline>
    );
  }

  return (
    <Timeline active={active} bulletSize={20} lineWidth={2}>
      {entries.map((entry, idx) => (
        <Timeline.Item
          key={`${entry.action}-${entry.timestamp}-${idx}`}
          bullet={<ClockIcon size={12} />}
          title={<Text size="sm" fw={600} ff="monospace" style={TNUM}>{dateTimeSecFormat(entry.timestamp)}</Text>}
        >
          <Stack gap={2}>
            <Text size="sm" fw={500}>{actionLabel(entry.action)}</Text>
            {entry.actorName ? <Text size="sm" c="dimmed">{t("timeline.by", { name: entry.actorName })}</Text> : null}
            {entry.notes ? <Text size="sm" c="dimmed">{noteLabel(entry.notes)}</Text> : null}
          </Stack>
        </Timeline.Item>
      ))}

      {pendingSteps.map((step) => (
        <Timeline.Item
          key={`pending-${step.status}`}
          color="gray"
          lineVariant="dashed"
          bullet={<CircleDashedIcon size={12} />}
          title={<Text size="sm" fw={500} c="dimmed">{step.label}</Text>}
        >
          <Text size="sm" c="dimmed">{t("timeline.pendingStep")}</Text>
        </Timeline.Item>
      ))}
    </Timeline>
  );
}
